import React from 'react';
import { UseMeasureRef } from 'react-use/lib/useMeasure';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { PieChart } from '../../../../../../CommonComponents/OPL/charts/PieChart/PieChart';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import { Card } from '../../../../../../CommonComponents/OPL/Card/Card';
import { BucketData } from '../../useAssetBucketingResults';
import { EmptyState } from '../../../../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { Item } from './components/Item/Item';
import { DraggableItem } from './components/DraggableItem/DraggableItem';
import { Droppable } from './components/Droppable/Droppable';
import PieChartImg from './img/PieChartImg.svg';
import { MAX_CHART_SIZE } from '../../utils/getBucketChartSizesDict';
import { Name } from './components/Name/Name';
import { Description } from './components/Description/Description';
import { RemoveAssetFn } from '../../../AssetBucketingEdit/AssetBucketingEdit.meta';

interface Props extends BucketData {
  isEditable?: boolean;
  bucketIndex?: number;
  descriptionElement: { ref?: UseMeasureRef<HTMLDivElement>; height: number };
  onRemoveAsset?: RemoveAssetFn;
}

export const Bucket = ({
  id,
  title,
  description,
  slicedData,
  total,
  isEditable,
  bucketIndex,
  chartSize,
  descriptionElement,
  onRemoveAsset,
  chartTotal,
}: Props) => {
  return (
    <Card p="16" height="100%" display="flex" flexDirection="column">
      {isEditable && bucketIndex !== undefined ? (
        <>
          <Name bucketIndex={bucketIndex} mb="16" />

          <Description bucketIndex={bucketIndex} mb="16" />
        </>
      ) : (
        <>
          <Text isOverflowWrap fontSize="h4" fontWeight={600} mb="8">
            {title}
          </Text>

          <Box height={descriptionElement.height} mb="32">
            <Text ref={descriptionElement.ref} isOverflowWrap color="neutral.500">
              {description}
            </Text>
          </Box>
        </>
      )}

      <Box
        display="flex"
        flex="initial"
        alignItems="center"
        justifyContent="center"
        height={MAX_CHART_SIZE}
        mx="auto"
        mb="32"
      >
        {slicedData.length === 0 ? (
          <>{isEditable ? <PieChartImg /> : <EmptyState variant="chart" />}</>
        ) : chartTotal === 0 ? (
          <PieChartImg />
        ) : (
          <Box width={chartSize} height={chartSize}>
            <PieChart
              series={[{ data: slicedData.map(data => ({ ...data, y: Math.abs(data.y) })) }]}
              customSettings={{
                plotOptions: {
                  pie: {
                    innerSize: '60%',
                    borderWidth: 1,
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        flex={slicedData.length === 0 && !isEditable ? 'initial' : 1}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          border="1px solid"
          borderColor="neutral.300"
          borderRadius="medium"
          px="16"
          py={20}
          my={16}
        >
          <Text fontSize="h6" fontWeight={600}>
            Total
          </Text>
          <Text variant="tabular" fontSize="h6" fontWeight={600}>
            {formatCurrency(total)}
          </Text>
        </Box>

        <>
          {isEditable ? (
            <Droppable id={id || title} bucketIndex={bucketIndex}>
              {slicedData.map(assetData => (
                <DraggableItem
                  key={assetData.id}
                  bucketIndex={bucketIndex}
                  onRemove={onRemoveAsset}
                  {...assetData}
                />
              ))}
            </Droppable>
          ) : (
            <Box as="ul" css="list-style: none" p="0">
              {slicedData.map(assetData => (
                <Item as="li" key={assetData.id} {...assetData} />
              ))}
            </Box>
          )}
        </>
      </Box>
    </Card>
  );
};
